<template lang="pug">
#other-office-connection-list.mb-0
  // other office information
  .row.other-office-base-info(v-cloak)
    .box
      .box-header.with-border.register-header
        .title
          span 他事業所連携情報一覧
        .d-flex.flex-row
          .d-flex.flex-column.flex-fill
          .buttons.d-flex.flex-row-reverse.justify-content-end.flex-wrap
            button.btn.back-btn(@click="onGotoTop") 戻る
      .box-body(style="padding-top:0;")
        OtherOfficeConnectionTable(v-model="items" :fields="fields" :totalRows.sync="rows"
          :currentPage.sync="currentPage" :perPage="perPage" :isLoading="isLoading" :filter="filter"
          @onEdit="gotoEdit" @onDelete="onDelete" @onResend="onResendInvitation")
        .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
          // 検索
          .search-filter.d-flex.mr-1.align-items-center.mr-2
            b-input-group.search-filter-input-group.mr-1(size="md")
              b-input-group-prepend
                b-input-group-text.search-filter-icon
                  .search-icon
              b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
            HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
              template(v-slot:text)
                div 検索項目は以下になります。
                div ・事業所名
                div ・担当者名
          // ページあたりの行数
          span.table-option-text 1ページの行数:
          .per-page
            b-form-select(id="per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
          // ページネーション
          b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage"
            hide-goto-end-buttons)

    OkCancelModal(v-model="showConfirmResendInvitationModal" title="招待メールを再送信しますか？" @ok="resendInvitation")
    OkModal(v-model="showResendInvitationSuccessModal" title="招待メールを再送信しました")

    CheckDeleteModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteConnection")
      template(#message)
        .message 削除前に以下をご確認ください。
        ul.confirm-list
          li 削除した事業所とのメッセージは削除され、閲覧・投稿・返信ができなくなります。
          li 削除した事業所にも削除されたことは通知されます。
    OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

    PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, editOtherOfficeConnectionStore } from "@/store/store";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";

import OtherOfficeConnectionsService, {
  OtherOfficeConnectionDeleteModel,
  OtherOfficeConnectionGetModel,
  OtherOfficeConnectionMailPostModel,
  SenderType
} from "@/commons/services/OtherOfficeConnections";
import { PlanType } from "@/commons/services/Plans";

import OtherOfficeConnectionTable, { FilterSetting, OtherOfficeConnectionTableField, OtherOfficeConnectionTableItem } from "@/pc/containers/base/OtherOfficeConnectionTable.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import CheckDeleteModal from "@/pc/components/modals/CheckDeleteModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";

/**
 * ### 他事業所連携サービス ホーム画面
 */
@Component({
  components: {
    OtherOfficeConnectionTable,
    OkCancelModal,
    OkModal,
    CheckDeleteModal,
    HelpIcon,
    PageLoading
  }
})
export default class OtherOfficeConnectionList extends Vue {
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 招待メール入力モーダルの入力結果 */
  invitationOtherOfficeConnection: { mailaddress: string; officeName: string; staffName: string } = {
    mailaddress: "",
    officeName: "",
    staffName: ""
  };

  /** 招待メール再送信確認モーダル表示可否 */
  showConfirmResendInvitationModal: boolean = false;
  /** 招待メール再送信完了モーダル表示可否 */
  showResendInvitationSuccessModal: boolean = false;

  /** 事業所削除モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 事業所削除モーダルメッセージ */
  deleteMessage: string = "";
  /** 削除対象の事業所 */
  deleteTargetConnectionMailaddress: string = "";
  /** 事業所削除成功モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** テーブル読込み中 */
  isLoading: boolean = true;
  /** テーブル列 */
  fields: Array<OtherOfficeConnectionTableField> = [];
  /** テーブル行 */
  items: Array<OtherOfficeConnectionTableItem> = [];

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.otherOffice.connectionList.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.otherOffice.connectionList.pageOptions;

  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  get _otherOfficeTopPageFunction(): boolean {
    return userStore.loginUser.permissions.functions.otherOfficeTopPageFunction;
  }

  /** ### プランがライト以上かを返す */
  get _isOverLightPlan(): boolean {
    const planType: PlanType = userStore.facility.planType;

    if (PlanType.LIGHT <= planType) {
      return true;
    } else {
      return false;
    }
  }

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `other-office-connectionList-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    const fields: Array<OtherOfficeConnectionTableField> = this.createFields();
    this.fields = fields;

    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.otherOffice.connectionList.perPage);

    await this.createView();

    return;
  }

  public createFields(): Array<OtherOfficeConnectionTableField> {
    const fields: Array<OtherOfficeConnectionTableField> = [];
    fields.push({ key: "status", label: "登録状況", sortable: false, sortDirection: "desc", class: "status text-center" });
    fields.push({ key: "isSender", label: "招待／被招待", sortable: false, sortDirection: "desc", class: "status text-center" });
    fields.push({ key: "officeName", label: "事業所名", sortable: false, sortDirection: "desc", class: "office-name" });
    fields.push({ key: "staffName", label: "担当者名", sortable: false, sortDirection: "desc", class: "staff-name" });
    fields.push({ key: "latestSendDatetime", label: "最終招待送信日時", sortable: false, sortDirection: "desc", class: "latest-send-datetime text-center" });
    if (this._otherOfficeTopPageFunction == true) {
      fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    }
    return fields;
  }

  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["other-office-connectionList-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  public async createView(): Promise<void> {
    this.isLoading = true;

    const connections: Array<OtherOfficeConnectionGetModel> = await this.getOtherOfficeConnections();
    const items: Array<OtherOfficeConnectionTableItem> = this.createItems(connections);
    this.items = items;
    this.rows = items.length;

    this.isLoading = false;
    return;
  }

  public async getOtherOfficeConnections(): Promise<Array<OtherOfficeConnectionGetModel>> {
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    return response;
  }

  public createItems(connections: Array<OtherOfficeConnectionGetModel>): Array<OtherOfficeConnectionTableItem> {
    const items: Array<OtherOfficeConnectionTableItem> = connections;
    return items;
  }

  /**
   * ### ソート
   *
   * - 未登録を先にする
   * - 未登録同士の場合、最終招待送信日時の昇順で並べる
   * - 登録済同士の場合、最終招待送信日時の昇順で並べる
   */
  public sortItems(items: Array<OtherOfficeConnectionTableItem>): Array<OtherOfficeConnectionTableItem> {
    const sortedItems: Array<OtherOfficeConnectionTableItem> = items.concat().sort((a, b) => {
      // 未登録同士の場合、最終招待送信日時の昇順で並べる
      if (a.registerd === false && b.registerd === false) {
        if (a.latestSendDatetime < b.latestSendDatetime) {
          return -1;
        }
        if (b.latestSendDatetime < a.latestSendDatetime) {
          return 1;
        }
      }
      // 未登録は無条件で先に並べる
      if (a.registerd === false) {
        return -1;
      }
      if (b.registerd === false) {
        return 1;
      }
      // 最終招待送信日時の昇順で並べる
      if (a.latestSendDatetime < b.latestSendDatetime) {
        return -1;
      }
      if (b.latestSendDatetime < a.latestSendDatetime) {
        return 1;
      }
      return 0;
    });
    return sortedItems;
  }

  onGotoTop(): void {
    this.$router.push(PcUrl.HomeForOtherOffice);
    return;
  }

  public gotoEdit(item: OtherOfficeConnectionTableItem): void {
    editOtherOfficeConnectionStore.setMailaddress({ mailaddress: item.mailaddress });
    this.$router.push(PcUrl.OtherOfficeConnectionEdit);
    return;
  }

  public onDelete(item: OtherOfficeConnectionTableItem): void {
    this.deleteMessage = `${item.officeName}（${item.staffName}）を削除しますか？`;
    this.showDeleteModal = true;
    this.deleteTargetConnectionMailaddress = item.mailaddress;
    return;
  }

  public async deleteConnection(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    const requestBody: OtherOfficeConnectionDeleteModel = this.createDeleteRequestBody();
    await this.otherOfficeConnectionsService.delete(userStore, requestBody);

    // 対象の行を削除して再リクエストはしない
    this.items = this.items.filter((item) => {
      if (item.mailaddress != this.deleteTargetConnectionMailaddress) {
        return true;
      } else {
        return false;
      }
    });

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  public createDeleteRequestBody(): OtherOfficeConnectionDeleteModel {
    const requestBody: OtherOfficeConnectionDeleteModel = {
      mailaddress: this.deleteTargetConnectionMailaddress
    };
    return requestBody;
  }

  public onResendInvitation(item: OtherOfficeConnectionTableItem): void {
    this.invitationOtherOfficeConnection = {
      mailaddress: item.mailaddress,
      officeName: item.officeName,
      staffName: item.staffName
    };
    this.showConfirmResendInvitationModal = true;
    return;
  }

  public async resendInvitation(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "再送信中...";

    const requestBody: OtherOfficeConnectionMailPostModel = this.createSendMailRequestBody(this.invitationOtherOfficeConnection, SenderType.RESEND);
    await this.otherOfficeConnectionsService.sendMail(userStore, requestBody);

    this.isBusy = false;
    this.showResendInvitationSuccessModal = true;
    return;
  }

  public selectPerPage(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  public selectPage(page: number): void {
    this.currentPage = page;
    return;
  }

  public createSendMailRequestBody(
    invitationOtherOfficeConnection: { mailaddress: string; officeName: string; staffName: string },
    sendType: SenderType
  ): OtherOfficeConnectionMailPostModel {
    return {
      sendType: sendType,
      mailaddress: invitationOtherOfficeConnection.mailaddress,
      officeName: invitationOtherOfficeConnection.officeName,
      staffName: invitationOtherOfficeConnection.staffName
    };
  }
}
</script>

<style lang="scss" scoped>
#other-office-connection-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .other-office-base-info {
    margin-left: -3px;
    margin-right: -3px;
    .home-box {
      padding-left: 3px;
      padding-right: 3px;
    }
  }

  .back-btn {
    width: 94px;
  }
}
</style>
